import React, { Fragment, useCallback, useEffect, useState } from "react";
import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
    getFacetedUniqueValues,
} from "@tanstack/react-table";
import { useVirtual } from "@tanstack/react-virtual";
import {
    ArrowDownCircleIcon,
    ArrowPathIcon,
    DocumentTextIcon,
    EyeIcon,
    FunnelIcon,
    ViewColumnsIcon
} from "@heroicons/react/24/outline";
import Popup from "../../Common/Popup";
import axios from "axios";
import { API_URL } from "../../../imports";
import {
    DebouncedInput,
    FinalizedFilter,
    IndeterminateCheckbox,
    SelectTextFilter,
    StatusFilter,
} from "../../Common/Filters";
import ReadModal from "../../Common/ReadModal";
import LogTable from "../../Common/LogTable";
import { handleApiError } from "../../Common/APIUtils";
import FetchingDataLoader from "../../Common/FetchingDataLoader";
import ClosingDataLoader from "../../Common/ClosingDataLoader";
import { formattedDate, generateProcessCodeFileNameAlreadyDT } from "../../Common/CommonUtils";
import { Menu, Transition } from "@headlessui/react";
import DropdownModal from "../../Common/DropdownModal";
import IndexBasedRangeSelection from "../../Common/IndexBasedRangeSelection";

export default function TranscriptionFilewiseStatusLogs({ startIndex, endIndex, setStartIndex, setEndIndex, rowSelection, setRowSelection }) {
    let [isOpenRead, setIsOpenRead] = useState(false);
    let [rowData, setRowData] = useState([]);
    let [isOpenPopup, setIsOpenPopup] = useState(false);
    let [msg, setMsg] = useState(false);
    let [isSuccess, setIsSuccess] = useState(false);
    let [modalTitle, setModalTitle] = useState(false);
    let [detailsState, setDetailsState] = useState(false);
    let [fileInfoData, setFileInfoData] = useState([]);
    const [Loader, setLoader] = useState(true);
    const [closeLoader, setCloseLoader] = useState(false);
    const [jsonValue, setJsonValue] = useState(null);

    function closePopup() {
        setIsOpenPopup(false);
        setCloseLoader(false);
    }

    const openPopup = useCallback(() => {
        setIsOpenPopup(true);
    }, [setIsOpenPopup]);

    function closeReadModal() {
        setIsOpenRead(false);
    }

    const FileDetailsData = useCallback(
        async (filename) => {
            try {
                const response = await axios.post(
                    `${API_URL}/Transcription/file_details_log/${sessionStorage.getItem(
                        "projectCode"
                    )}`,
                    { filename: filename },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem(
                                "access_token"
                            )}`,
                        },
                    }
                );
                // Define the desired order of keys
                const keyOrder = [
                    "Status",
                    "Status Code",
                    "Status Message",
                    "Comment",
                    "Params",
                    "Merged",
                    "Finalized",
                    "Start Date",
                    "Execution Date",
                    "Processed By",
                    "Payload Lang",
                    "Model",
                    "Response",
                    "id",
                ];

                // Map over the entries and create objects with the desired key order
                const formattedData = response.data.data.map((entry) => {
                    const formattedEntry = {};
                    keyOrder.forEach((key) => {
                        formattedEntry[key] = entry[key];
                    });
                    return formattedEntry;
                });
                setFileInfoData(formattedData);
            } catch (error) {
                const errorMessage = handleApiError(error);
                setMsg(errorMessage);
                setIsSuccess(false);
                openPopup();
            }
        },
        [setFileInfoData, setMsg, setIsSuccess, openPopup]
    );

    const getProcessResponse = useCallback(
        async (id, process_code, isOriginal) => {
            setJsonValue({});
            axios
                .post(
                    `${API_URL}/entries/getresponse`,
                    {
                        id: id,
                        process_code: process_code,
                        proj_code: sessionStorage.getItem("projectCode"),
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem(
                                "access_token"
                            )}`,
                        },
                    }
                )
                .then((response) => {
                    // Decode the base64 string
                    const decodedJsonString = atob(response.data.data);
                    const parsedData = JSON.parse(decodedJsonString);
                    if (parsedData) {
                        if (isOriginal) {
                            if ("original_lang_transcribed_text" in parsedData) {
                                setJsonValue(parsedData.original_lang_transcribed_text);
                            } else {
                                setJsonValue("No original transcript available");
                            }
                        } else {
                            if ("original_lang_transcribed_text" in parsedData) {
                                const { original_lang_transcribed_text, ...rest } = parsedData;
                                setJsonValue(rest);
                            } else {
                                setJsonValue(parsedData);
                            }
                        }
                    } else {
                        setJsonValue("No response available");
                    }
                })
                .catch((error) => {
                    setJsonValue(null);
                    const errorMessage = handleApiError(error);
                    setMsg(errorMessage);
                    setIsSuccess(false);
                    openPopup();
                });
        },
        [openPopup]
    );

    const openReadModal = useCallback(
        (e, options) => {
            setRowData(e);
            if (options === "response") {
                setModalTitle(`Response of ${e.filename}`);
                setDetailsState(false);
                getProcessResponse(e.id, "TRANSCRIPT", false)
                // Decode the base64 string
                // const decodedJsonString = atob(e.response);
                // setJsonValue(JSON.parse(decodedJsonString));
            } else if (options === "originalText") {
                setModalTitle(`Original Transcript of ${e.filename}`);
                setDetailsState(false);
                getProcessResponse(e.id, "TRANSCRIPT", true);
            } else {
                setModalTitle(`${e.filename}`);
                setDetailsState(true);
                FileDetailsData(e.filename);
            }
            setIsOpenRead(true);
        },
        [
            setRowData,
            setModalTitle,
            setDetailsState,
            FileDetailsData,
            setIsOpenRead,
            getProcessResponse,
        ]
    );

    const ReloadData = async () => {
        setRowSelection({});
        setStartIndex(undefined);
        setEndIndex(undefined);
        setLoader(true);
        axios
            .get(
                `${API_URL}/Transcription/filewisestatusrpt/${sessionStorage.getItem(
                    "projectCode"
                )}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "access_token"
                        )}`,
                    },
                    maxContentLength: Infinity,
                    maxBodyLength: Infinity,
                }
            )
            .then((response) => {
                setData(response.data.data);
            })
            .catch((error) => {
                const errorMessage = handleApiError(error);
                setMsg(errorMessage);
                setIsSuccess(false);
                openPopup();
            })
            .finally(() => {
                setTimeout(() => {
                    setLoader(false);
                }, 100);
            });
    };

    const MergeAPI = async (mergeData) => {
        setRowSelection({});
        axios
            .post(
                `${API_URL}/Transcription/MergeSelectedTranscripts/${sessionStorage.getItem(
                    "projectCode"
                )}`,
                {
                    fileslist: mergeData
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "access_token"
                        )}`,
                    },
                }
            )
            .then((response) => {
                setMsg(response.data.message);
                setIsSuccess(true);
                openPopup();
                ReloadData();
            })
            .catch((error) => {
                const errorMessage = handleApiError(error);
                setMsg(errorMessage);
                setIsSuccess(false);
                openPopup();
            });
    };
    const MergeAllWithoutSelectionAPI = async () => {
        axios
            .get(
                `${API_URL}/Transcription/MergeAllTranscripts/${sessionStorage.getItem(
                    "projectCode"
                )}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "access_token"
                        )}`,
                    },
                }
            )
            .then((response) => {
                setMsg(response.data.message);
                setIsSuccess(true);
                openPopup();
                ReloadData();
            })
            .catch((error) => {
                const errorMessage = handleApiError(error);
                setMsg(errorMessage);
                setIsSuccess(false);
                openPopup();
            });
    };
    const handleSelectedMerge = () => {
        // Filter the Files based on the rowSelection
        const selectedMergeFiles = Object.keys(rowSelection)
            .filter((index) => rowSelection[index])
            .map((index) => data[index]);
        if (
            selectedMergeFiles.length > 0 &&
            selectedMergeFiles.length !== data.length
        ) {
            const fileInfo = selectedMergeFiles.map((file) => {
                const container = {};
                container["id"] = file.id;
                container["filename"] = file.filename;
                return container;
            });
            MergeAPI(fileInfo);
        } else if (selectedMergeFiles.length === data.length) {
            MergeAllWithoutSelectionAPI();
        }
    };

    const CloseAPI = async (closeData) => {
        setLoader(true);
        setCloseLoader(true);
        setRowSelection({});
        axios
            .post(
                `${API_URL}/Transcription/closefiles/${sessionStorage.getItem(
                    "projectCode"
                )}`,
                { fileslist: closeData },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "access_token"
                        )}`,
                    },
                }
            )
            .then((response) => {
                setMsg(response.data.message);
                setIsSuccess(true);
                setTimeout(() => {
                    openPopup();
                    ReloadData();
                }, 100);
            })
            .catch((error) => {
                const errorMessage = handleApiError(error);
                setMsg(errorMessage);
                setIsSuccess(false);
                openPopup();
                ReloadData();
            });
    };
    const handleSelectedClose = () => {
        // Filter the Files based on the rowSelection
        const selectedCloseFiles = Object.keys(rowSelection)
            .filter((index) => rowSelection[index])
            .map((index) => data[index]);
        if (selectedCloseFiles.length > 0) {
            const fileNames = selectedCloseFiles.map((file) => file.filename);
            CloseAPI(fileNames);
        }
    };

    const DocDownloadButton = ({ id, fileName, disabledState }) => {
        const createDocxFile = () => {
            axios.post(
                `${API_URL}/Transcription/downloaddoc`,
                {
                    id: id,
                    proj_code: sessionStorage.getItem("projectCode"),
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "access_token"
                        )}`,
                    },
                }
            )
                .then((response) => {
                    // Decode the base64 string
                    const byteCharacters = atob(response.data.data);
                    const byteArrays = [];
                    // Convert base64 string to binary data
                    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                        const slice = byteCharacters.slice(offset, offset + 512);
                        const byteNumbers = new Array(slice.length);
                        for (let i = 0; i < slice.length; i++) {
                            byteNumbers[i] = slice.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        byteArrays.push(byteArray);
                    }
                    // Create a Blob from binary data
                    const blob = new Blob(byteArrays, { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileName + ".docx";
                    link.click();
                }).catch((error) => {
                    console.log(error)
                    const errorMessage = handleApiError(error);
                    setMsg(errorMessage);
                    setIsSuccess(false);
                    openPopup();
                });
        };

        return (
            <button
                className="p-1 rounded-md border disabled:text-gray-300"
                disabled={disabledState}
                onClick={createDocxFile}
            >
                <p className="flex gap-1" title="docx file download">
                    <DocumentTextIcon className="h-4 w-auto" />
                </p>
            </button>
        );
    };

    const OriginalTextDocDownloadButton = ({ id, fileName, disabledState }) => {
        const createOriginalTextDocxFile = () => {
            axios
                .post(
                    `${API_URL}/Transcription/downloaddoc_original`,
                    {
                        id: id,
                        proj_code: sessionStorage.getItem("projectCode"),
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
                        },
                    }
                )
                .then((response) => {
                    // Decode the base64 string
                    const byteCharacters = atob(response.data.data);
                    const byteArrays = [];
                    // Convert base64 string to binary data
                    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                        const slice = byteCharacters.slice(offset, offset + 512);
                        const byteNumbers = new Array(slice.length);
                        for (let i = 0; i < slice.length; i++) {
                            byteNumbers[i] = slice.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        byteArrays.push(byteArray);
                    }
                    // Create a Blob from binary data
                    const blob = new Blob(byteArrays, { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileName + "_original_transcribed_text.docx";
                    link.click();
                })
                .catch((error) => {
                    console.log(error);
                    const errorMessage = handleApiError(error);
                    setMsg(errorMessage);
                    setIsSuccess(false);
                    openPopup();
                });
        };
    
        return (
            <button
                className="p-1 rounded-md border disabled:text-gray-300"
                disabled={disabledState}
                onClick={createOriginalTextDocxFile}
            >
                <p className="flex gap-1" title="original trans text docx file download">
                    {/* <DocumentIcon  className="h-4 w-auto" /> */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-languages">
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/>
                        <path d="M14 2v4a2 2 0 0 0 2 2h4"/>
                        <g transform="translate(6.5,7) scale(0.6)">
                            <path d="m0 2 6 6"/>
                            <path d="m-1 8 6-6 2-3"/>
                            <path d="M-3 1h12"/>
                            <path d="M2 -2h1"/>
                            <path d="m15 16-5-10-5 10"/>
                            <path d="M7 12h6"/>
                        </g>
                    </svg>
                </p>
            </button>
        );
    };    


    const JsonDownloadButton = ({ id, JSONfileName, disabledState }) => {
        const downloadJson = () => {
            axios
                .post(
                    `${API_URL}/entries/getresponse`,
                    {
                        id: id,
                        process_code: "TRANSCRIPT",
                        proj_code: sessionStorage.getItem("projectCode"),
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem(
                                "access_token"
                            )}`,
                        },
                    }
                )
                .then((response) => {
                    // Decode the base64 string
                    const decodedJsonString = atob(response.data.data);
                    const blob = new Blob(
                        [JSON.stringify(JSON.parse(decodedJsonString))],
                        {
                            type: "application/json",
                        }
                    );
                    const link = document.createElement("a");
                    link.href = window.URL.createObjectURL(blob);
                    link.download = JSONfileName + ".json";
                    link.click();
                })
                .catch((error) => {
                    const errorMessage = handleApiError(error);
                    setMsg(errorMessage);
                    setIsSuccess(false);
                    openPopup();
                });
        };

        return (
            <button
                className="p-1 rounded-md border disabled:text-gray-300"
                onClick={downloadJson}
                disabled={disabledState}
            >
                <p className="flex gap-1" title="json file download">
                    <ArrowDownCircleIcon className="h-4" />
                </p>
            </button>
        );
    };

    const DownloadCSVAPI = async (files_list, latest) => {
        setRowSelection({});
        axios
            .post(
                `${API_URL}/Transcription/downloadcsv/${sessionStorage.getItem(
                    "projectCode"
                )}`,
                { files_list: files_list, latest: latest },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(
                            "access_token"
                        )}`,
                    },
                }
            )
            .then((response) => {
                // Decode the base64-encoded content to binary
                const binaryContent = atob(response.data.download_content);

                // Create a Uint8Array from the binary content
                const uint8Array = new Uint8Array(binaryContent.length);
                for (let i = 0; i < binaryContent.length; i++) {
                    uint8Array[i] = binaryContent.charCodeAt(i);
                }

                // Create a Blob from the Uint8Array
                const blob = new Blob([uint8Array], { type: "text/csv" }); // Adjust type based on your file type

                // Create a URL object from the Blob
                const url = window.URL.createObjectURL(blob);

                // Create a link element
                const link = document.createElement("a");
                link.href = url;
                if (latest === true) { link.download = `Transcription_${sessionStorage.getItem("projectName")}_latest_logs.csv`; }
                else { link.download = `Transcription_${sessionStorage.getItem("projectName")}_logs.csv`; }

                // Append the link to the document body
                document.body.appendChild(link);

                // Trigger the download
                link.click();

                // Cleanup
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                const errorMessage = handleApiError(error);
                setMsg(errorMessage);
                setIsSuccess(false);
                openPopup();
            });
    };

    const handleDownloadCSV = (latest) => {
        // Filter the Files based on the rowSelection
        const selectedFiles = Object.keys(rowSelection)
            .filter((index) => rowSelection[index])
            .map((index) => data[index]);
        if (latest === true) {
            if (selectedFiles.length > 0 && selectedFiles.length !== data.length) {
                const fileIds = selectedFiles.map((file) => file.id);
                DownloadCSVAPI(fileIds, true);
            } else if (selectedFiles.length === data.length) {
                DownloadCSVAPI("All", true);
            } else {
                DownloadCSVAPI("All", true);
            }
        }
        else {
            if (selectedFiles.length > 0 && selectedFiles.length !== data.length) {
                const filenames = selectedFiles.map((file) => file.filename);
                DownloadCSVAPI(filenames, false);
            } else if (selectedFiles.length === data.length) {
                DownloadCSVAPI("All", false);
            } else {
                DownloadCSVAPI("All", false);
            }
        }
    };

    const cols = [
        { id: "err_log", name: "Status" },
        { id: "status_code", name: "Status Code" },
        { id: "status_message", name: "Status Message" },
        { id: "confidence_score", name: "Confidence Score" },
        { id: "transcribed_detected_lang", name: "Detected Lang" },
        { id: "merge", name: "Merged" },
        { id: "closing", name: "Finalize" },
        { id: "start_date_time", name: "Start Date" },
        { id: "creation_date", name: "End Date" },
        { id: "transcription_model", name: "Model" },
        { id: "user_id", name: "Processed By" },
        { id: "id", name: "Actions" },
    ];

    const columnNames = cols.reduce((acc, curr) => {
        acc[curr.id] = curr.name;
        return acc;
    }, {});

    const [sorting, setSorting] = React.useState([]);

    // Update rowSelection based on start and end index inputs
    const handleSetSelection = () => {
        if (startIndex !== null && endIndex !== null && startIndex <= endIndex) {
            const newSelection = {};
            for (let i = startIndex - 1; i <= endIndex - 1; i++) {
                newSelection[i] = true;  // Mark these indexes as selected
            }
            setRowSelection(newSelection);
        }
    };

    const initialcolumnVisibility = {
        status_code: false,
        confidence_score: false,
        transcribed_detected_lang: false,
        user_id: false,
    };

    const columns = React.useMemo(
        () => [
            {
                id: "select",
                size: 10,
                header: ({ table }) => (
                    <IndeterminateCheckbox
                        {...{
                            checked: table.getIsAllRowsSelected(),
                            indeterminate: table.getIsSomeRowsSelected(),
                            onChange: table.getToggleAllRowsSelectedHandler(),
                        }}
                    />
                ),
                cell: ({ row }) => (
                    <center className="px-1">
                        <IndeterminateCheckbox
                            {...{
                                checked: row.getIsSelected(),
                                disabled: !row.getCanSelect(),
                                indeterminate: row.getIsSomeSelected(),
                                onChange: row.getToggleSelectedHandler(),
                            }}
                        />
                    </center>
                ),
            },
            {
                accessorKey: "index",
                enableColumnFilter: false,
                enableSorting: false,
                size: 50,
                header: "#",
                cell: ({ row }) => <center>{row.index + 1}</center>,
            },
            {
                accessorKey: "filename",
                enableColumnFilter: false,
                size: 1000,
                header: () => <span>File Name</span>,
                cell: ({ row }) => (
                    <div
                        title="Click to get file details"
                        onClick={() =>
                            openReadModal(row.original, "filedetails")
                        }
                        disabled={row.original.response ? false : true}
                        className="text-blue-600 cursor-default lg:cursor-pointer disabled:text-black"
                    >
                        {row.original.filename}
                    </div>
                ),
            },
            {
                accessorKey: "err_log",
                enableColumnFilter: true,
                filterFn: "equals",
                size: 50,
                header: () => <span>Status</span>,
                cell: ({ row }) => (
                    <div className="flex justify-center">
                        <div
                            className={`${row.original.err_log === "success" &&
                                "bg-green-500"
                                } ${row.original.err_log === "error" && "bg-red-500"
                                } ${row.original.err_log === "Unprocessed" &&
                                "bg-yellow-500"
                                }  ${row.original.err_log !== "success" &&
                                row.original.err_log !== "error" &&
                                row.original.err_log !== "Unprocessed" &&
                                "bg-yellow-500"
                                } 
                            text-white text-xs w-fit px-2 py-1 font-bold rounded-xl flex flex-nowrap`}
                        >
                            {row.original.err_log && row.original.err_log}
                            {!row.original.err_log && "Unprocessed"}
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: "status_code",
                enableColumnFilter: false,
                size: 50,
                header: () => <span>Status Code</span>,
                cell: ({ row }) => <center>{row.original.status_code}</center>,
            },
            {
                accessorKey: "status_message",
                enableColumnFilter: false,
                size: 50,
                header: () => <span>Status Message</span>,
                cell: ({ row }) => (
                    <span title={row.original.status_message}>
                        {row.original.status_message !== null
                            ? row.original.status_message.slice(0, 20) +
                            (row.original.status_message.length > 20
                                ? "..."
                                : "")
                            : null}
                    </span>
                ),
            },
            {
                accessorKey: "confidence_score",
                enableColumnFilter: false,
                size: 50,
                header: () => <span>Confidence Score</span>,
                cell: ({ row }) => (
                    <center>
                        {row.original.confidence_score === null
                            ? null
                            : row.original.confidence_score.toFixed(2)}
                    </center>
                ),
            },
            {
                accessorKey: "transcribed_detected_lang",
                enableColumnFilter: false,
                size: 50,
                header: () => <span>Detected Lang</span>,
            },
            {
                accessorKey: "merge",
                enableColumnFilter: false,
                size: 50,
                header: () => <span>Merged</span>,
                cell: ({ row }) => (
                    <center>
                        {row.original.merge !== "0" ? row.original.merge : null}
                    </center>
                ),
            },
            {
                accessorKey: "closing",
                enableColumnFilter: true,
                filterFn: "equals",
                size: 50,
                header: () => <span>Finalized</span>,
                cell: ({ row }) => (
                    <center>{row.original.closing === true ? 1 : ""}</center>
                ),
            },
            {
                accessorKey: "start_date_time",
                enableColumnFilter: false,
                size: 50,
                header: "Start Date",
                cell: ({ row }) => <div>{formattedDate(row.original.start_date_time)}</div>,
            },
            {
                accessorKey: "creation_date",
                enableColumnFilter: false,
                size: 50,
                header: "End Date",
                cell: ({ row }) => <div>{formattedDate(row.original.creation_date)}</div>,
            },
            {
                accessorKey: "payload_lang",
                enableColumnFilter: true,
                filterFn: "equals",
                size: 50,
                header: "Payload Lang",
                cell: ({ row }) => <div>{row.original.payload_lang}</div>,
            },
            {
                accessorKey: "transcription_model",
                enableColumnFilter: true,
                filterFn: "equals",
                size: 50,
                header: "Model",
                cell: ({ row }) => (
                    <div>{row.original.transcription_model}</div>
                ),
            },
            {
                accessorKey: "user_id",
                enableColumnFilter: false,
                size: 50,
                header: "Processed By",
                cell: ({ row }) => (
                    <span className="capitalize" title={row.original.user_id}>
                        {row.original.user_id !== null
                            ? row.original.user_id.slice(0, 12) +
                            (row.original.user_id.length > 12 ? "..." : "")
                            : null}
                    </span>
                ),
            },
            {
                accessorKey: "id",
                header: "Actions",
                size: 50,
                enableColumnFilter: false,
                enableSorting: false,
                cell: ({ row }) => (
                    <div className="flex gap-2 items-center justify-center">
                        <button
                            className="p-1 rounded-md border disabled:text-gray-300 text-green-800"
                            disabled={row.original.status_code ? false : true}
                            onClick={() =>
                                openReadModal(row.original, "response")
                            }
                            title="view response"
                        >
                            <EyeIcon className="h-4" />
                        </button>
                        <button
                            className="p-1 rounded-md border disabled:text-gray-300 text-green-800"
                            disabled={row.original.status_code ? false : true}
                            onClick={() => openReadModal(row.original, "originalText")}
                            title="original transcribe text"
                        >
                             {/* <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-file-type"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M9 13v-1h6v1" /><path d="M12 12v6" /><path d="M11 18h2" /></svg> */}
                             {/* <ViewfinderCircleIcon className="h-4" /> */}
                             <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-languages"><path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/></svg>
                        </button>
                        <JsonDownloadButton
                            id={row.original.id}
                            JSONfileName={
                                row.original.status_code === "0" &&
                                row.original.creation_date &&
                                generateProcessCodeFileNameAlreadyDT(
                                    row.original.filename,
                                    row.original.creation_date
                                )
                            }
                            disabledState={
                                row.original.status_code === "0" ? false : true
                            }
                        />
                        <DocDownloadButton id={row.original.id} fileName={row.original.filename.split('.').slice(0, -1).join('.')} disabledState={row.original.status_code === "0" ? false : true} />
                        <OriginalTextDocDownloadButton id={row.original.id} fileName={row.original.filename.split('.').slice(0, -1).join('.')} disabledState={row.original.status_code === "0" ? false : true}/>
                    </div>
                ),
            },
        ],
        [openReadModal]
    );

    const [data, setData] = useState([]);

    const [globalFilter, setGlobalFilter] = React.useState("");

    const table = useReactTable({
        data,
        columns,
        initialState: {
            columnVisibility: initialcolumnVisibility,
        },
        state: {
            rowSelection,
            sorting,
            globalFilter,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    const renderTableCellIndex = (row) => {
        return (
            table.getFilteredRowModel().rows.indexOf(row) + 1 ||
            table.getSortedRowModel().rows.indexOf(row) + 1 ||
            row.index + 1
        );
    };

    const tableContainerRef = React.useRef(null);

    const { rows } = table.getRowModel();
    const rowVirtualizer = useVirtual({
        parentRef: tableContainerRef,
        size: rows.length,
        overscan: data.length,
    });
    const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

    const paddingTop = virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
    const paddingBottom =
        virtualRows.length > 0
            ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
            : 0;

    useEffect(() => {
        const fetchInitialData = async () => {
            axios
                .get(
                    `${API_URL}/Transcription/filewisestatusrpt/${sessionStorage.getItem(
                        "projectCode"
                    )}`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem(
                                "access_token"
                            )}`,
                        },
                        maxContentLength: Infinity,
                        maxBodyLength: Infinity,
                    }
                )
                .then((response) => {
                    setData(response.data.data);
                })
                .catch((error) => {
                    const errorMessage = handleApiError(error);
                    setMsg(errorMessage);
                    setIsSuccess(false);
                    openPopup();
                })
                .finally(() => {
                    setTimeout(() => {
                        setLoader(false);
                    }, 100);
                });
        };
        fetchInitialData();
    }, [openPopup]);

    return (
        <div className="m-2 border rounded-lg bg-white font-GoogleSans tracking-wider overflow-hidden">
            <Popup
                isOpen={isOpenPopup}
                msg={msg}
                closeModal={closePopup}
                isSuccess={isSuccess}
            />
            <ReadModal
                isOpen={isOpenRead}
                closeModal={closeReadModal}
                title={modalTitle}
                modalInputs={
                    <>
                        {detailsState && (
                            <div className="overflow-auto h-[calc(100vh-8rem)] md:h-[calc(100vh-8rem)]">
                                <LogTable
                                    data={fileInfoData}
                                    filename={rowData.filename}
                                    process_code="TRANSCRIPT"
                                />
                            </div>
                        )}
                        {!detailsState && (
                            <div className="overflow-auto h-[calc(100vh-8rem)] md:h-[calc(100vh-8rem)]">
                                {jsonValue && (
                                    <pre className="pretxtwrap text-sm">
                                        {JSON.stringify(jsonValue, null, 2)}
                                    </pre>
                                )}
                                {!jsonValue && "No Response Found."}
                            </div>
                        )}
                    </>
                }
            />
            <div className="flex flex-wrap justify-between m-3 gap-2">
                <DebouncedInput
                    value={globalFilter ?? ""}
                    onChange={(value) => setGlobalFilter(String(value))}
                    placeholder="Search..."
                    wclass="w-full md:w-auto 2xl:w-[20rem]"
                />
                <div className="flex flex-wrap gap-2">
                    <IndexBasedRangeSelection startIndex={startIndex} setStartIndex={setStartIndex} endIndex={endIndex} setEndIndex={setEndIndex} handleSetSelection={handleSetSelection} max={data.length} />
                    <button
                        className="rounded-md cursor-default lg:cursor-pointer p-2 inline-flex text-sm border-gray-300 border"
                        onClick={() => ReloadData()}
                    >
                        <ArrowPathIcon className="h-5 text-green-700" />
                    </button>
                    <DropdownModal
                        icon={<ViewColumnsIcon className="h-5 w-auto" />}
                        body={table
                            .getAllLeafColumns()
                            .filter((column) =>
                                cols.map((col) => col.id).includes(column.id)
                            )
                            .map((column) => {
                                return (
                                    <div key={column.id} className="p-0.5 px-1">
                                        <label>
                                            <input
                                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                {...{
                                                    type: "checkbox",
                                                    checked:
                                                        column.getIsVisible(),
                                                    onChange:
                                                        column.getToggleVisibilityHandler(),
                                                }}
                                            />
                                            &nbsp;&nbsp;
                                            <span className="text-xs font-medium">{columnNames[column.id]}</span>
                                        </label>
                                    </div>
                                );
                            })}
                    />
                    <Menu as="div" className="relative">
                        <div>
                            <Menu.Button className="relative justify-center border border-gray-300 rounded-md p-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer">
                                <span className="absolute -inset-1.5" />
                                <FunnelIcon className="h-5 w-auto" />
                            </Menu.Button>
                        </div>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="fixed right-8 z-30 mt-3 w-fit px-2 pb-2 space-y-3 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <div>
                                    {data.length > 0 &&
                                        table
                                            .getHeaderGroups()
                                            .map((headerGroup) => (
                                                <div
                                                    key={headerGroup.id}
                                                    className="space-y-1"
                                                >
                                                    {headerGroup.headers.map(
                                                        (header) => (
                                                            <div
                                                                key={header.id}
                                                            >
                                                                {header.column.getCanFilter() &&
                                                                    header.column
                                                                        .id ===
                                                                    "err_log" ? (
                                                                    <div>
                                                                        <label
                                                                            htmlFor="push-everything"
                                                                            className="block text-xs font-bold tracking-widest leading-6 text-gray-400 ps-1"
                                                                        >
                                                                            Status
                                                                        </label>
                                                                        <StatusFilter
                                                                            column={
                                                                                header.column
                                                                            }
                                                                            table={
                                                                                table
                                                                            }
                                                                        />
                                                                    </div>
                                                                ) : null}
                                                                {header.column.getCanFilter() &&
                                                                    header.column
                                                                        .id ===
                                                                    "closing" ? (
                                                                    <div>
                                                                        <label
                                                                            htmlFor="push-everything"
                                                                            className="block text-xs font-bold tracking-widest leading-6 text-gray-400 ps-1"
                                                                        >
                                                                            Finalize
                                                                        </label>
                                                                        <FinalizedFilter
                                                                            column={
                                                                                header.column
                                                                            }
                                                                            table={
                                                                                table
                                                                            }
                                                                        />
                                                                    </div>
                                                                ) : null}
                                                                {header.column.getCanFilter() &&
                                                                    header.column
                                                                        .id ===
                                                                    "transcription_model" ? (
                                                                    <div>
                                                                        <label
                                                                            htmlFor="push-everything"
                                                                            className="block text-xs font-bold tracking-widest leading-6 text-gray-400 ps-1"
                                                                        >
                                                                            Model
                                                                        </label>
                                                                        <SelectTextFilter
                                                                            column={
                                                                                header.column
                                                                            }
                                                                            table={
                                                                                table
                                                                            }
                                                                        />
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            ))}
                                </div>
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            </div>
            {Loader && (!closeLoader ? <FetchingDataLoader /> : <ClosingDataLoader />)}
            {!Loader && (
                <>
                    <div
                        ref={tableContainerRef}
                        className="resp-processtable-h border-y overflow-auto"
                    >
                        <table className="flex-inline w-full border-collapse">
                            <thead className="text-sm lg:text-base bg-gray-200 border-b">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <th
                                                    className="border p-2 whitespace-nowrap"
                                                    key={header.id}
                                                    colSpan={header.colSpan}
                                                    style={{
                                                        width: header.getSize(),
                                                    }}
                                                >
                                                    {header.isPlaceholder ? null : (
                                                        <>
                                                            <div
                                                                className={`${header.column.getCanSort() &&
                                                                    header
                                                                        .column
                                                                        .id !==
                                                                    "err_log"
                                                                    ? "cursor-pointer select-none justify-start"
                                                                    : "justify-center"
                                                                    } flex`}
                                                                onClick={header.column.getToggleSortingHandler()}
                                                            >
                                                                {flexRender(
                                                                    header
                                                                        .column
                                                                        .columnDef
                                                                        .header,
                                                                    header.getContext()
                                                                )}
                                                                {{
                                                                    asc: " 🔼",
                                                                    desc: " 🔽",
                                                                }[
                                                                    header.column.getIsSorted()
                                                                ] || null}
                                                            </div>
                                                        </>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {
                                    rows.length === 0 ?
                                        (
                                            <tr>
                                                <td className='border p-2' colSpan={table.getHeaderGroups()[0].headers.length} style={{ textAlign: 'center' }}>
                                                    No Data Found
                                                </td>
                                            </tr>
                                        )
                                        :
                                        (
                                            <>
                                                {paddingTop > 0 && (
                                                    <tr>
                                                        <td
                                                            className="border p-2"
                                                            style={{
                                                                height: `${paddingTop}px`,
                                                            }}
                                                        />
                                                    </tr>
                                                )}
                                                {virtualRows.map((virtualRow) => {
                                                    const row = rows[virtualRow.index];
                                                    return (
                                                        <tr
                                                            key={row.id}
                                                            className="bg-white border-b transition duration-300 ease-in-out hover:bg-gray-100 text-sm font-medium"
                                                        >
                                                            {row
                                                                .getVisibleCells()
                                                                .map((cell) => {
                                                                    return (
                                                                        <td
                                                                            className="border p-2 whitespace-nowrap"
                                                                            key={cell.id}
                                                                        >
                                                                            {cell.column.id ===
                                                                                "index" && (
                                                                                    <center>
                                                                                        {renderTableCellIndex(
                                                                                            row
                                                                                        )}
                                                                                    </center>
                                                                                )}
                                                                            {cell.column.id !==
                                                                                "index" &&
                                                                                flexRender(
                                                                                    cell.column
                                                                                        .columnDef
                                                                                        .cell,
                                                                                    cell.getContext()
                                                                                )}
                                                                        </td>
                                                                    );
                                                                })}
                                                        </tr>
                                                    );
                                                })}
                                                {paddingBottom > 0 && (
                                                    <tr>
                                                        <td
                                                            className="border p-2"
                                                            style={{
                                                                height: `${paddingBottom}px`,
                                                            }}
                                                        />
                                                    </tr>
                                                )}
                                            </>
                                        )
                                }
                            </tbody>
                        </table>
                    </div>
                    {Object.keys(rowSelection).length > 0 && (
                        <div className="flex flex-wrap lg:justify-between w-full gap-2 p-3">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className="inline-flex whitespace-nowrap justify-center rounded-lg border border-transparent bg-blue-100 px-2 md:px-4 py-2 text-xs md:text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer"
                                    onClick={handleSelectedMerge}
                                >
                                    Merge
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex whitespace-nowrap justify-center rounded-lg border border-transparent bg-blue-100 px-2 md:px-4 py-2 text-xs md:text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer"
                                    onClick={handleSelectedClose}
                                >
                                    Finalize
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex whitespace-nowrap justify-center rounded-lg border border-transparent bg-blue-100 px-2 md:px-4 py-2 text-xs md:text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer"
                                    onClick={() => handleDownloadCSV(true)}
                                >
                                    Download Latest Logs
                                </button>
                            </div>
                            <button
                                type="button"
                                className="inline-flex whitespace-nowrap justify-center rounded-lg border border-transparent bg-blue-100 px-2 md:px-4 py-2 text-xs md:text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-default lg:cursor-pointer"
                                onClick={() => handleDownloadCSV(false)}
                            >
                                Download All Logs
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
